import { formatSydneyDate } from './sydneyDateTime'
/**
 * Map live board-pack figures onto the formatted Board Report JSON schema.
 * Patron names are replaced with Patron A/B aliases.
 */

function ragTone(rag) {
  return rag === 'red' ? 'red' : rag === 'amber' ? 'amber' : 'ok'
}

function longDate(iso) {
  const d = iso ? new Date(iso) : new Date()
  if (Number.isNaN(d.getTime())) return String(iso)
  return formatSydneyDate(d)
}

function shortDate(iso) {
  if (!iso) return '—'
  try {
    return formatSydneyDate(new Date(iso))
  } catch {
    return '—'
  }
}

function money(n) {
  return `$${Number(n || 0).toLocaleString('en-AU', { maximumFractionDigits: 0 })}`
}

function pretty(s) {
  const t = String(s || '').replace(/_/g, ' ').trim()
  return t ? t.charAt(0).toUpperCase() + t.slice(1) : '—'
}

function clip(s, n) {
  const t = String(s || '')
  return t.length > n ? `${t.slice(0, n - 1)}…` : t
}

function hasChartValues(items) {
  return Array.isArray(items) && items.some((i) => Number(i.value) > 0)
}

function filingPill(status) {
  const s = String(status || '').toUpperCase()
  if (s.includes('ACKNOWLEDG')) return { text: 'Acknowledged', tone: 'ok' }
  if (s.includes('SUBMIT')) return { text: 'Submitted', tone: 'ok' }
  if (s.includes('OVERDUE') || s.includes('LATE')) return { text: 'Overdue', tone: 'red' }
  if (s.includes('DRAFT')) return { text: 'Draft', tone: 'amber' }
  return { text: clip(status || 'Open', 13), tone: 'neutral' }
}

function casePill(row) {
  if (Number(row.idleDays) >= 14) return { text: 'Stalled', tone: 'red' }
  if (!row.owner) return { text: 'Unassigned', tone: 'amber' }
  return { text: clip(pretty(row.status), 13), tone: 'ok' }
}

function oblPill(status) {
  if (status === 'overdue') return { text: 'Ask questions', tone: 'red' }
  if (status === 'due_soon') return { text: 'Working on it', tone: 'amber' }
  return { text: 'OK', tone: 'ok' }
}

function venueTone(rating, score) {
  if (rating === 'High' || score >= 70) return 'red'
  if (rating === 'Medium' || score >= 40) return 'amber'
  return 'ok'
}

function makeAliases() {
  const map = new Map()
  return (key) => {
    const k = String(key || '').trim()
    if (!k || k === '—') return '—'
    if (!map.has(k)) {
      const n = map.size
      map.set(k, n < 26 ? `Patron ${String.fromCharCode(65 + n)}` : `Patron ${n + 1}`)
    }
    return map.get(k)
  }
}

export function buildFormattedBoardReportData(m) {
  const alias = makeAliases()
  const period = m.period || 'Board pack'
  const asAt = longDate(m.reportDate)
  const lamps = [
    m.invRag, m.pepRag, m.eftRag, m.payeeRag, m.hrRag, m.progRag, m.fileRag, m.peopleRag, m.opsRag,
  ]
  const rag = {
    questions: lamps.filter((r) => r === 'red').length,
    working: lamps.filter((r) => r === 'amber').length,
    ok: lamps.filter((r) => r === 'green').length,
  }

  const attention = []
  const stalled = m.investigation?.stalledDetail
  if (m.stalled > 0) {
    attention.push({
      tone: stalled && !stalled.assignee ? 'red' : 'amber',
      title: m.stalled === 1 ? 'One investigation is stalled' : `${m.stalled} investigations are stalled`,
      detail: stalled
        ? `${stalled.id || 'Open case'} · ${pretty(stalled.stage)}${stalled.stageDays ? ` · ${stalled.stageDays}d since last update` : ''}${stalled.assignee ? '' : ' · unassigned'}.`
        : 'Open cases have not moved. The board should ask who owns them and when they close.',
    })
  }
  if (m.pepUnreviewed > 0) {
    attention.push({
      tone: 'red',
      title: `${m.pepUnreviewed} PEP detection${m.pepUnreviewed === 1 ? '' : 's'} unreviewed`,
      detail: 'New PEP matches this week have not been reviewed. Confirm disposition before the next filing cycle.',
    })
  }
  if (m.oblOverdue > 0) {
    attention.push({
      tone: 'red',
      title: `${m.oblOverdue} program obligation${m.oblOverdue === 1 ? '' : 's'} overdue`,
      detail: 'Overdue program items sit on the obligations slide. The board should ask for a date and an owner.',
    })
  }
  if (m.ttrOverdue > 0) {
    attention.push({
      tone: 'red',
      title: `${m.ttrOverdue} TTR${m.ttrOverdue === 1 ? '' : 's'} overdue`,
      detail: 'Threshold reports past due need a filing date. Late TTRs are a regulatory question.',
    })
  }
  if (m.ecddGaps > 0) {
    attention.push({
      tone: 'red',
      title: `${m.ecddGaps} high-risk patron${m.ecddGaps === 1 ? '' : 's'} without ECDD`,
      detail: 'Enhanced due diligence is incomplete on high-risk patrons. Ask when the gaps close.',
    })
  }
  if (m.sof > 0) {
    attention.push({
      tone: 'amber',
      title: `${m.sof} source-of-funds request${m.sof === 1 ? '' : 's'} open`,
      detail: 'SoF remains open on payouts over threshold. Confirm what is outstanding and who is chasing it.',
    })
  }
  if (m.repeats > 0) {
    attention.push({
      tone: 'amber',
      title: `${m.repeats} repeat payee${m.repeats === 1 ? '' : 's'} in 30 days`,
      detail: 'Three or more payouts to the same payee in 30 days. Review whether the pattern is expected.',
    })
  }
  if (m.trainOverdue > 0) {
    attention.push({
      tone: 'amber',
      title: `${m.trainOverdue} staff training overdue`,
      detail: `Overall training is ${m.trainPct}%. Overdue staff are listed on the people section.`,
    })
  }

  const decisions = []
  if (stalled && !stalled.assignee) {
    decisions.push({
      title: 'Assign the stalled case',
      detail: `${stalled.id || 'The stalled investigation'} has no owner. Name an investigator and a next update date.`,
    })
  } else if (m.stalled > 0) {
    decisions.push({
      title: 'Unblock stalled investigations',
      detail: `${m.stalled} case${m.stalled === 1 ? '' : 's'} have not moved. Confirm the next action and a close-or-escalate date.`,
    })
  }
  if (m.pepUnreviewed > 0) {
    decisions.push({
      title: 'Review new PEP detections',
      detail: `${m.pepUnreviewed} unreviewed this week. Record a disposition before the next board pack.`,
    })
  }
  if (m.oblOverdue > 0) {
    decisions.push({
      title: 'Clear overdue obligations',
      detail: `${m.oblOverdue} program item${m.oblOverdue === 1 ? '' : 's'} overdue. Agree an owner and a completion date.`,
    })
  }
  if (m.ttrOverdue > 0) {
    decisions.push({
      title: 'File overdue TTRs',
      detail: `${m.ttrOverdue} threshold report${m.ttrOverdue === 1 ? '' : 's'} past due. Confirm lodgement this week.`,
    })
  }
  if (m.ecddGaps > 0) {
    decisions.push({
      title: 'Close ECDD gaps',
      detail: `${m.ecddGaps} high-risk patron${m.ecddGaps === 1 ? '' : 's'} still need enhanced due diligence.`,
    })
  }
  if (!decisions.length) {
    decisions.push({
      title: 'Note the pack',
      detail: 'No red items require a decision this period. Amber items stay with management.',
    })
  }

  const typology = (m.pack?.smr?.typology || []).map((r) => ({
    label: clip(r.label, 16),
    value: r.count,
    tone: 'neutral',
  }))
  const filings = (m.pack?.smr?.filings || []).map((r) => ({
    cells: [r.ref || '—', clip(r.venue || r.type || '—', 14)],
    pill: filingPill(r.status),
    dot: /overdue|late/i.test(r.status || '') ? 'red' : /draft/i.test(r.status || '') ? 'amber' : 'ok',
  }))

  const stageItems = (m.pack?.cases?.stageSplit || []).map((r) => ({
    label: clip(pretty(r.stage), 16),
    value: r.count,
  }))
  if (m.stalled > 0 && !stageItems.some((s) => /stall/i.test(s.label))) {
    stageItems.push({ label: 'Stalled', value: m.stalled })
  }
  const cases = (m.pack?.cases?.list || []).map((r) => ({
    cells: [r.caseNumber || '—', alias(r.patron || r.caseNumber)],
    pill: casePill(r),
    dot: Number(r.idleDays) >= 14 ? 'red' : r.owner ? 'ok' : 'amber',
  }))

  const riskItems = (m.patron?.riskBands || []).map((b) => ({
    label: clip(b.label, 16),
    value: b.count,
    tone: /crit|high/i.test(b.label || '') ? 'red' : /med/i.test(b.label || '') ? 'amber' : 'ok',
  }))
  const highRisk = (m.pack?.highRiskList || m.pack?.newHighRisk || []).map((r) => ({
    cells: [r.id || r.memberId || '—', alias(r.id || r.memberId || r.name), r.score == null ? '—' : String(Math.round(Number(r.score)))],
    pill: Number(r.score) >= 70 ? { text: 'High', tone: 'red' } : { text: 'Watch', tone: 'amber' },
    dot: Number(r.score) >= 70 ? 'red' : 'amber',
  }))
  const pepRows = (m.pep?.recent || []).map((r) => ({
    cells: [shortDate(r.detectedAt), alias(r.memberId || r.name), clip(r.category || 'PEP', 16)],
    pill: r.unreviewed ? { text: 'Unreviewed', tone: 'red' } : { text: 'Noted', tone: 'amber' },
    dot: r.unreviewed ? 'red' : 'amber',
  }))

  const payoutItems = (m.payouts?.weekly || []).map((w) => ({
    label: w.label,
    value: (Number(w.eft) || 0) + (Number(w.cheque) || 0),
  }))
  const payoutRows = (m.payouts?.list || []).map((r) => ({
    cells: [shortDate(r.at), alias(r.memberId || r.name), money(r.amount)],
    pill: /sof|pending|open/i.test(r.status || '') ? { text: 'SoF open', tone: 'amber' } : { text: clip(r.status || 'Paid', 13), tone: 'ok' },
    dot: /sof|pending|open/i.test(r.status || '') ? 'amber' : 'ok',
  }))
  const payeeRows = (m.payouts?.repeatPayees || []).map((r) => ({
    cells: [alias(r.name), String(r.times), money(r.value), clip(r.venue || '—', 14)],
    pill: r.times >= 5 ? { text: 'High freq', tone: 'red' } : { text: 'Repeat', tone: 'amber' },
    dot: r.times >= 5 ? 'red' : 'amber',
  }))

  const trainRows = (m.pack?.overdueTraining || []).map((r) => ({
    cells: [clip(r.staff || '—', 18), clip(r.role || '—', 14), clip(r.venue || '—', 14), String(r.overdue)],
    pill: Number(r.overdue) >= 2 ? { text: 'Overdue', tone: 'red' } : { text: 'Overdue', tone: 'amber' },
    dot: Number(r.overdue) >= 2 ? 'red' : 'amber',
  }))

  const venueItems = (m.venueRisk || []).map((v) => ({
    label: clip(v.name, 16),
    value: Number(v.score) || 0,
    tone: venueTone(v.rating, v.score),
  }))
  const venueRows = (m.venueRisk || []).map((v) => ({
    cells: [clip(v.name, 18), `${v.score ?? '—'} /100`],
    pill: { text: clip(v.rating || 'Low', 13), tone: venueTone(v.rating, v.score) },
    dot: venueTone(v.rating, v.score),
  }))

  const oblItems = [
    { label: 'Current', value: m.obligations?.current || 0, tone: 'ok' },
    { label: 'Due soon', value: m.obligations?.dueSoon || 0, tone: 'amber' },
    { label: 'Overdue', value: m.obligations?.overdue || 0, tone: 'red' },
  ]
  const oblRows = (m.obligations?.items || []).map((o) => ({
    cells: [clip(o.name, 20), clip(pretty(o.category), 14)],
    pill: oblPill(o.status),
    dot: o.status === 'overdue' ? 'red' : o.status === 'due_soon' ? 'amber' : 'ok',
  }))

  const checklistRows = (m.checklists || []).slice(0, 20).map((c) => ({
    cells: [clip(c.name, 24), `${c.progress || 0}/${c.total || 0}`, pretty(c.status)],
    pill: /progress|not_started|open/i.test(c.status || '') ? { text: 'Open', tone: 'amber' } : { text: 'Done', tone: 'ok' },
    dot: /progress|not_started|open/i.test(c.status || '') ? 'amber' : 'ok',
  }))

  const sections = [
    {
      id: 'filings',
      title: 'Regulatory filings',
      subtitle: m.smr?.filedThisQuarter
        ? `${m.smrPct}% of SMRs this quarter were on time.`
        : 'No SMRs filed this quarter.',
      stats: [
        { value: String(m.smr?.filedThisQuarter || 0), label: 'Filed this Q', status: ragTone(m.fileRag), sub: `${m.smrPct}% on time` },
        { value: String(m.smr?.filedLate || 0), label: 'Filed late', status: m.smr?.filedLate ? 'amber' : 'ok' },
        { value: String(m.ttrOpen), label: 'TTR to file', status: m.ttrOverdue > 0 ? 'red' : m.ttrOpen > 0 ? 'amber' : 'ok', sub: m.ttrOverdue ? `${m.ttrOverdue} overdue` : `${m.ttrFiled7d} last 7d` },
        { value: String(m.smr?.avgDaysToFile ?? '—'), label: 'Avg days to file', status: 'neutral' },
      ],
      ...(hasChartValues(typology) ? {
        chart: { type: 'bar', label: 'What the SMRs were about', items: typology },
      } : {}),
      ...(filings.length ? {
        table: {
          label: 'Latest lodgements',
          hasPill: true,
          columns: [
            { w: 1.55, mono: true, bold: true },
            { w: 1.00, muted: true },
          ],
          rows: filings,
        },
      } : {}),
      note: {
        kicker: 'Why no patron names here',
        body: 'Board packs reference filings by ID. Patron identities stay in the operational register.',
      },
    },
    {
      id: 'investigations',
      title: 'Investigations',
      subtitle: m.stalled
        ? `${m.investigation?.openCases || 0} open · ${m.stalled} stalled.`
        : `${m.investigation?.openCases || 0} open cases. None stalled.`,
      stats: [
        { value: String(m.investigation?.openCases || 0), label: 'Open cases', status: ragTone(m.invRag), sub: m.investigation?.avgAgeOpenDays ? `Avg age ${m.investigation.avgAgeOpenDays}d` : 'None open' },
        { value: String(m.stalled), label: 'Stalled', status: ragTone(m.invRag) },
        { value: String(m.investigation?.closedThisQuarter || 0), label: 'Closed this Q', status: 'neutral' },
        { value: String(m.investigation?.escalatedToSMR || 0), label: 'Escalated to SMR', status: 'neutral' },
      ],
      ...(stalled ? {
        callout: {
          tone: stalled.assignee ? 'amber' : 'red',
          kicker: 'STALLED',
          text: `${stalled.id || 'Open case'} · ${pretty(stalled.stage)}${stalled.stageDays ? ` · ${stalled.stageDays}d` : ''}${stalled.assignee ? '' : ' · unassigned'}.`,
        },
      } : {}),
      ...(hasChartValues(stageItems) ? {
        chart: { type: 'bar', label: 'Open cases by stage', items: stageItems },
      } : {}),
      ...(cases.length ? {
        table: {
          label: 'Open cases',
          hasPill: true,
          columns: [
            { w: 1.50, mono: true, bold: true },
            { w: 1.05 },
          ],
          rows: cases,
        },
      } : {}),
    },
    {
      id: 'patrons',
      title: 'Patrons and screening',
      subtitle: m.ecddGaps
        ? `${m.highRisk} high-risk · ${m.ecddGaps} without ECDD.`
        : `${m.highRisk} high-risk patrons. ECDD current.`,
      stats: [
        { value: String(m.highRisk), label: 'High-risk', status: m.ecddGaps > 0 ? 'amber' : 'ok', sub: m.ecddGaps ? `${m.ecddGaps} ECDD gaps` : 'ECDD current' },
        { value: String(m.pepUnreviewed), label: 'PEP unreviewed', status: ragTone(m.pepRag) },
        { value: String(m.venueExcl), label: 'Venue exclusions', status: 'ok', sub: `${m.venueExclQ} this quarter` },
        { value: String(m.newHr), label: 'New high-risk', status: ragTone(m.hrRag), sub: 'Added this month' },
      ],
      ...(hasChartValues(riskItems) ? {
        chart: {
          type: 'doughnut',
          label: 'Risk mix',
          centerValue: String(m.highRisk),
          centerLabel: 'High-risk',
          items: riskItems,
        },
      } : {}),
      ...(highRisk.length ? {
        table: {
          label: 'High-risk patrons',
          hasPill: true,
          columns: [
            { w: 1.10, mono: true, bold: true },
            { w: 0.70 },
            { w: 0.55, align: 'right' },
          ],
          rows: highRisk,
        },
      } : {}),
    },
    ...(pepRows.length ? [{
      id: 'pep',
      title: 'PEP detections',
      subtitle: m.pepUnreviewed ? `${m.pepUnreviewed} unreviewed this week.` : 'No unreviewed PEP detections this week.',
      table: {
        label: 'Latest detections',
        hasPill: true,
        columns: [
          { w: 1.1, muted: true },
          { w: 1.2, bold: true },
          { w: 1.4 },
        ],
        rows: pepRows,
      },
    }] : []),
    {
      id: 'payouts',
      title: 'Payouts',
      subtitle: m.sof
        ? `${m.large} over threshold this month · ${m.sof} SoF open.`
        : `${m.large} EFT / cheque over threshold this month.`,
      stats: [
        { value: String(m.large), label: 'EFT / cheque', status: ragTone(m.eftRag), sub: 'Over $5,000' },
        { value: String(m.sof), label: 'SoF open', status: m.sof > 0 ? 'amber' : 'ok' },
        { value: String(m.repeats), label: 'Repeat payees', status: ragTone(m.payeeRag), sub: '3+ in 30 days' },
        { value: money(m.payouts?.totalValue || 0), label: 'Value this month', status: 'neutral' },
      ],
      ...(hasChartValues(payoutItems) ? {
        chart: { type: 'bar', label: 'Weekly EFT and cheque count', items: payoutItems },
      } : {}),
      ...(payoutRows.length ? {
        table: {
          label: 'Large payouts',
          hasPill: true,
          columns: [
            { w: 0.70, muted: true },
            { w: 0.80 },
            { w: 0.85, align: 'right', bold: true },
          ],
          rows: payoutRows,
        },
      } : {}),
    },
    ...(payeeRows.length ? [{
      id: 'payees',
      title: 'Repeat payees',
      subtitle: 'Three or more payouts to the same payee in 30 days.',
      table: {
        label: 'Repeat payees — 30 days',
        hasPill: true,
        columns: [
          { w: 1.3, bold: true },
          { w: 0.55, align: 'right' },
          { w: 0.9, align: 'right' },
          { w: 1.1, muted: true },
        ],
        rows: payeeRows,
      },
    }] : []),
    {
      id: 'people',
      title: 'People — training and EDD',
      subtitle: `Training ${m.trainPct}% · EDD ${m.edd?.totalStaff ? `${m.edd.eddPct}%` : 'not reported'}.`,
      stats: [
        { value: `${m.trainPct}%`, label: 'Training', status: m.trainPct >= 95 || m.trainPct === 0 ? 'ok' : 'amber', sub: `${m.trainCompleted} done` },
        { value: String(m.trainOverdue), label: 'Overdue staff', status: m.trainOverdue > 0 ? 'amber' : 'ok' },
        { value: m.edd?.totalStaff ? `${m.edd.eddPct}%` : '—', label: 'EDD completion', status: m.edd?.overdue > 0 ? 'amber' : 'ok', sub: m.edd?.totalStaff ? `${m.edd.eddCompleted} of ${m.edd.totalStaff}` : 'Not reported' },
        { value: String(m.ecddGaps), label: 'ECDD gaps', status: m.ecddGaps > 0 ? 'red' : 'ok' },
      ],
      ...(trainRows.length ? {
        table: {
          label: 'Overdue training',
          hasPill: true,
          columns: [
            { w: 1.5, bold: true },
            { w: 1.1, muted: true },
            { w: 1.1, muted: true },
            { w: 0.5, align: 'right' },
          ],
          rows: trainRows,
        },
      } : {}),
    },
    {
      id: 'program',
      title: 'Program, venue and risk',
      subtitle: `${m.highest?.name || '—'} residual ${m.highest?.score ?? '—'} · appetite: no High.`,
      stats: [
        { value: `${m.oblPct}%`, label: 'Obligations current', status: ragTone(m.progRag), sub: `${m.obligations?.current || 0} of ${m.oblTotal}` },
        { value: String(m.highest?.score ?? '—'), label: 'Highest residual', status: m.highest?.score >= 70 ? 'red' : m.highest?.score >= 40 ? 'amber' : 'ok', sub: clip(m.highest?.name, 22) },
        { value: String(m.riskOpen), label: 'Risk register', status: m.riskOverdue > 0 ? 'red' : m.riskOpen > 0 ? 'amber' : 'ok', sub: `${m.riskOverdue} overdue reviews` },
        { value: String(m.openChecklists), label: 'Open checklists', status: ragTone(m.opsRag) },
      ],
      ...(hasChartValues(venueItems) ? {
        chart: { type: 'bar', label: 'Venue residual / 100', items: venueItems },
      } : {}),
      ...(venueRows.length ? {
        table: {
          label: 'Venue risk ratings',
          hasPill: true,
          columns: [
            { w: 1.65, bold: true },
            { w: 0.80, align: 'right' },
          ],
          rows: venueRows,
        },
      } : {}),
    },
    {
      id: 'obligations',
      title: 'Program obligations',
      subtitle: 'Same register as the Program donut, split so the board works red and amber first.',
      stats: [
        { value: String(m.obligations?.overdue || 0), label: 'Ask questions', status: m.oblOverdue > 0 ? 'red' : 'ok' },
        { value: String(m.obligations?.dueSoon || 0), label: 'Working on it', status: m.oblDue > 0 ? 'amber' : 'ok' },
        { value: String(m.obligations?.current || 0), label: 'OK', status: 'ok' },
        { value: `${m.oblPct}%`, label: 'Current', status: ragTone(m.progRag) },
      ],
      ...(hasChartValues(oblItems) ? {
        chart: {
          type: 'doughnut',
          label: 'Obligation status',
          centerValue: `${m.oblPct}%`,
          centerLabel: 'Current',
          items: oblItems,
        },
      } : {}),
      ...(oblRows.length ? {
        table: {
          label: 'Live register',
          hasPill: true,
          columns: [
            { w: 1.60, bold: true },
            { w: 0.90, muted: true },
          ],
          rows: oblRows,
        },
      } : {}),
    },
    ...(checklistRows.length ? [{
      id: 'operations',
      title: 'Operations',
      subtitle: `${m.openChecklists} open checklists · ${m.docsCurrent} documents current.`,
      stats: [
        { value: String(m.openChecklists), label: 'Open checklists', status: ragTone(m.opsRag) },
        { value: `${m.docsCurrent}/${(m.documents || []).length}`, label: 'Docs current', status: 'neutral' },
        { value: String(m.rulesNeedReview), label: 'Rules to review', status: m.rulesNeedReview > 0 ? 'amber' : 'ok' },
        { value: String((m.reviews || []).length), label: 'Independent reviews', status: (m.reviews || []).length ? 'ok' : 'amber' },
      ],
      table: {
        label: 'Active checklists',
        hasPill: true,
        columns: [
          { w: 2.2, bold: true },
          { w: 0.7, align: 'right' },
          { w: 1.1, muted: true },
        ],
        rows: checklistRows,
      },
    }] : []),
  ]

  return {
    meta: {
      period,
      asAt,
      deckName: `Board AML/CTF Report — ${period}`,
      brand: 'PaySafeGo',
      headline: 'AML/CTF at a glance.',
      lede: rag.questions
        ? 'Red items on the next pages need a question at the table.'
        : rag.working
          ? 'Amber is being worked. Green is on track.'
          : 'Green is on track. Nothing on this pack needs a board decision.',
      rag,
    },
    execTitle: 'The quarter in one view',
    execSubtitle: 'Green is noted, amber is managed, red is on this page for a decision.',
    attentionLabel: 'Where we need the board',
    kpis: [
      { value: String(m.investigation?.openCases || 0), label: 'Investigations open', status: ragTone(m.invRag), sub: m.investigation?.avgAgeOpenDays ? `avg age ${m.investigation.avgAgeOpenDays} days` : 'no open cases' },
      { value: String(m.highRisk), label: 'High-risk patrons', status: m.ecddGaps > 0 ? 'amber' : 'ok', sub: m.ecddGaps ? `${m.ecddGaps} without ECDD` : 'ECDD current' },
      { value: `${m.smrPct}%`, label: 'SMR on time', status: ragTone(m.fileRag), sub: `${m.smr?.filedThisQuarter || 0} filed this Q` },
      { value: `${m.trainPct}%`, label: 'Training', status: m.trainPct >= 95 || m.trainPct === 0 ? 'ok' : 'amber', sub: `${m.trainOverdue} overdue` },
      { value: `${m.oblPct}%`, label: 'Obligations current', status: ragTone(m.progRag), sub: `${m.oblDue} due soon` },
    ],
    attention,
    sections,
    decisionsTitle: 'What we’re asking the board.',
    decisions,
  }
}
